import { _ as __rest } from './_tslib.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useMemo, useRef, useEffect } from 'react';
import { ModalUnstyled } from '@mui/base';
import { generateClasses, platform, startFocus, toggleMobileFocusTrap, ensureScrollableAccess } from '@ids/tools';
import { s as styleInject } from './style-inject.es.js';
import { u as useId } from './use-id.js';
import { I as IdsIconButton } from './icon-button.js';

var css_248z = "/*\n** Voxel Foundation Formats\n*/\n/*\n** Voxel Foundation Media Query Mixins\n*/\n.ids-reset-bankline-header-style, .ids-modal-header {\n  background: initial;\n  height: initial;\n  position: initial;\n  width: initial;\n}\n\n.ids-reset-bankline-footer-style, .ids-modal-footer {\n  background: initial;\n  border: initial;\n  height: initial;\n  margin: initial;\n  min-width: initial;\n  position: initial;\n  width: initial;\n}\n\n.ids-modal-wrapper {\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  isolation: isolate;\n  max-height: 92vh;\n  min-width: 272px;\n}\n\n.ids-modal {\n  background-color: var(--ids_color_bg_base);\n  border-radius: var(--ids_size_general_8x);\n  container-name: idsmodal;\n  container-type: inline-size;\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  -webkit-box-orient: vertical;\n  -webkit-box-direction: normal;\n      -ms-flex-direction: column;\n          flex-direction: column;\n  max-height: 100%;\n  max-width: 100%;\n  min-width: 272px;\n  overflow: hidden;\n}\n.ids-modal__body {\n  outline-offset: -0.0625rem;\n  overflow-y: auto;\n}\n\n.ids-modal-header {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  gap: var(--ids_spacing_2x);\n  -webkit-box-pack: justify;\n      -ms-flex-pack: justify;\n          justify-content: space-between;\n  padding: var(--ids_spacing_2x);\n  padding-left: var(--ids_spacing_3x);\n}\n.ids-modal-header__title {\n  font: var(--ids_textStyle_f01_s20_h28_wrg);\n}\n.ids-modal-header__description {\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n  margin-top: var(--ids_spacing_1x);\n}\n\n.ids-modal-footer {\n  background-color: var(--ids_color_bg_base);\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  -webkit-box-orient: vertical;\n  -webkit-box-direction: normal;\n      -ms-flex-flow: column wrap;\n          flex-flow: column wrap;\n  gap: var(--ids_spacing_4x);\n  -webkit-box-pack: end;\n      -ms-flex-pack: end;\n          justify-content: flex-end;\n  padding: var(--ids_spacing_4x);\n  position: relative;\n}\n@container idsmodal (inline-size > 552px) {\n  .ids-modal-footer {\n    -webkit-box-orient: horizontal;\n    -webkit-box-direction: normal;\n        -ms-flex-direction: row;\n            flex-direction: row;\n  }\n}\n\n.ids-overlay {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  height: 100vh;\n  -webkit-box-pack: center;\n      -ms-flex-pack: center;\n          justify-content: center;\n  left: 0;\n  position: fixed;\n  top: 0;\n  width: 100vw;\n  z-index: 1001;\n}\n\n.ids-backdrop {\n  background-color: var(--ids_color_action_neutral_variant_medium);\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  height: 100vh;\n  left: 0;\n  position: fixed;\n  top: 0;\n  width: 100vw;\n}";
styleInject(css_248z);

const IdsModalHeader = ({
  description,
  children,
  id,
  setOpen,
  noDivider,
  noCloseButton
}) => {
  const memoizedClose = useMemo(() => {
    if (noCloseButton) {
      return null;
    }
    return jsx(IdsIconButton, Object.assign({
      "aria-label": "fechar",
      id: `${id}-close`,
      onClick: () => setOpen(false)
    }, {
      children: "fechar"
    }));
  }, [setOpen, id, noCloseButton]);
  return jsxs(Fragment, {
    children: [jsxs("header", Object.assign({
      className: "ids-modal-header"
    }, {
      children: [jsxs("div", {
        children: [children && jsx("h2", Object.assign({
          className: "ids-modal-header__title",
          id: `${id}-title`
        }, {
          children: children
        })), description && jsx("p", Object.assign({
          className: "ids-modal-header__description"
        }, {
          children: description
        }))]
      }), memoizedClose]
    })), !noDivider && jsx("hr", {
      "aria-hidden": "true",
      className: "ids-divider -regular -soft"
    })]
  });
};

const IdsModalFooter = ({
  children
}) => jsxs(Fragment, {
  children: [jsx("hr", {
    "aria-hidden": "true",
    className: "ids-divider -regular -soft"
  }), jsx("footer", Object.assign({
    className: "ids-modal-footer"
  }, {
    children: children
  }))]
});

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsModal } from '@ids/react';
 * ```
 */
/* istanbul-ignore-next */
const IdsModal = _a => {
  var {
      'aria-labelledby': ariaLabelledby,
      width,
      children,
      open,
      setOpen,
      className = '',
      id: externalId,
      title,
      description,
      footer,
      headerProps,
      noDivider,
      noCloseButton
    } = _a,
    props = __rest(_a, ['aria-labelledby', "width", "children", "open", "setOpen", "className", "id", "title", "description", "footer", "headerProps", "noDivider", "noCloseButton"]);
  const classes = generateClasses({
    'ids-modal': true,
    [className]: className
  });
  const ref = useRef();
  const bodyRef = useRef();
  const id = useId(externalId, 'ids-react-modal');
  const ariaModal = platform.os !== 'IOS';
  const memoizedHeader = useMemo(() => {
    if (!title && noCloseButton) {
      return null;
    }
    return jsx(IdsModalHeader, Object.assign({
      setOpen: setOpen,
      description: description,
      noDivider: noDivider,
      noCloseButton: noCloseButton,
      id: id
    }, headerProps, {
      children: title
    }));
  }, [title, noCloseButton, setOpen, description, noDivider, id, headerProps]);
  useEffect(() => {
    setTimeout(() => {
      if (!ref.current || !bodyRef.current) {
        /* istanbul ignore next */
        return;
      }
      if (open) {
        startFocus(ref.current);
        toggleMobileFocusTrap(ref.current);
        ensureScrollableAccess(bodyRef.current);
        return;
      }
      toggleMobileFocusTrap(ref.current, false);
    });
  }, [open]);
  useEffect(() => () => {
    /* istanbul ignore next */
    if (ref.current) {
      toggleMobileFocusTrap(ref.current, false);
    }
  }, []);
  return jsx(ModalUnstyled, Object.assign({
    disableAutoFocus: true,
    open: open,
    className: "ids-overlay",
    onClose: /* istanbul ignore next */
    () => setOpen(false),
    slots: {
      backdrop: 'div'
    },
    slotProps: {
      backdrop: {
        className: 'ids-backdrop'
      }
    }
  }, {
    children: jsx("div", Object.assign({
      className: "ids-modal-wrapper"
    }, {
      children: jsx("div", Object.assign({
        className: "ids-container-fluid"
      }, {
        children: jsxs("section", Object.assign({
          ref: ref,
          className: classes,
          style: {
            width
          },
          role: "dialog",
          "aria-labelledby": ariaLabelledby || `${id}-title`,
          id: id,
          tabIndex: -1,
          "aria-modal": ariaModal
        }, props, {
          children: [memoizedHeader, jsx("div", Object.assign({
            className: "ids-modal__body",
            ref: bodyRef
          }, {
            children: children
          })), footer && jsx(IdsModalFooter, {
            children: footer
          })]
        }))
      }))
    }))
  }));
};

export { IdsModal as I };
//# sourceMappingURL=modal.js.map
